import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, inject, resource } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SCOPED_ACCESS_GATE } from '@mb/auth/client';
import { firstValueFromAbortable } from '@mb/common/client';
import type { ApiResponse } from '@mb/common';
import type {
  CreateOrganizationInput,
  OrganizationDto,
  OrganizationUnitDto,
  UpdateOrganizationInput,
} from '../../shared';

export interface OrganizationFormScopeDto {
  organizations: OrganizationDto[];
  organizationUnits: OrganizationUnitDto[];
}

@Injectable({ providedIn: 'root' })
export class OrganizationAdminApiService {
  private readonly http = inject(HttpClient);
  /**
   * Which tenant is on screen — the one chosen in the top bar.
   *
   * Through the gate rather than the context store: the store lives beside
   * this service and importing it here would run `services → state →
   * services` in a circle.
   */
  private readonly access = inject(SCOPED_ACCESS_GATE);

  private static readonly BASE = '/api/v1/organization/organizations';

  listOrganizations(options?: {
    signal?: AbortSignal;
  }): Promise<OrganizationDto[]> {
    return firstValueFromAbortable(
      this.http.get<OrganizationDto[]>(OrganizationAdminApiService.BASE),
      options?.signal,
    );
  }

  /**
   * What the tenant on screen consists of: its organization and its units.
   *
   * Always scoped to the selected tenant. This used to return every
   * organization the user may see together with the units of all of them,
   * which put other tenants' units into pickers that only mean anything
   * within one — three entries reading "My Organization", one of which had
   * the contracts and no way to tell which.
   *
   * Idle until a tenant is known, rather than loading everything meanwhile:
   * a picker that briefly offers the wrong units is worse than one that is
   * briefly empty.
   */
  organizationFormScopeResource(enabled: () => boolean) {
    return resource({
      params: () => {
        const organizationId = this.access.selectedOrganizationId();
        return enabled() && organizationId ? organizationId : undefined;
      },
      loader: async ({
        params: organizationId,
        abortSignal,
      }): Promise<OrganizationFormScopeDto> => {
        const [organizations, organizationUnits] = await Promise.all([
          this.listOrganizations({ signal: abortSignal }),
          this.listOrganizationUnits(organizationId, { signal: abortSignal }),
        ]);

        return {
          organizations: organizations.filter(
            (organization) => organization.id === organizationId,
          ),
          organizationUnits,
        };
      },
    });
  }

  getOrganization(
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<OrganizationDto> {
    return firstValueFromAbortable(
      this.http.get<OrganizationDto>(
        `${OrganizationAdminApiService.BASE}/${id}`,
      ),
      options?.signal,
    );
  }

  organizationResource(id: () => string | undefined) {
    return httpResource<OrganizationDto>(() => {
      const value = id();
      return value
        ? {
            url: `${OrganizationAdminApiService.BASE}/${value}`,
            method: 'GET' as const,
            withCredentials: true,
          }
        : undefined;
    });
  }

  createOrganization(
    payload: CreateOrganizationInput,
  ): Promise<ApiResponse<OrganizationDto>> {
    return firstValueFrom(
      this.http.post<ApiResponse<OrganizationDto>>(
        OrganizationAdminApiService.BASE,
        payload,
      ),
    );
  }

  updateOrganization(
    payload: UpdateOrganizationInput,
  ): Promise<ApiResponse<OrganizationDto>> {
    return firstValueFrom(
      this.http.patch<ApiResponse<OrganizationDto>>(
        `${OrganizationAdminApiService.BASE}/${payload.id}`,
        payload,
      ),
    );
  }

  deleteOrganization(id: string): Promise<ApiResponse<unknown>> {
    return firstValueFrom(
      this.http.delete<ApiResponse<unknown>>(
        `${OrganizationAdminApiService.BASE}/${id}`,
      ),
    );
  }

  listOrganizationUnits(
    organizationId: string,
    options?: { signal?: AbortSignal },
  ): Promise<OrganizationUnitDto[]> {
    return firstValueFromAbortable(
      this.http.get<OrganizationUnitDto[]>('/api/v1/organization/units', {
        params: { organizationId },
      }),
      options?.signal,
    );
  }
}
